import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (phone, password) => api.post('/auth/login', { phone, password });
export const getMe = () => api.get('/auth/me');

// Users
export const getUsers = () => api.get('/users');
export const createUser = (data) => api.post('/users', data);
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/users/${id}`);

// Locations
export const getLocations = () => api.get('/locations');
export const getLocation = (id) => api.get(`/locations/${id}`);
export const createLocation = (data) => api.post('/locations', data);
export const updateLocation = (id, data) => api.put(`/locations/${id}`, data);

// Import/Export Templates
export const downloadLocationsTemplate = () => `${API_URL}/api/templates/locations`;
export const downloadOpeningBalancesTemplate = () => `${API_URL}/api/templates/opening-balances`;

export const importLocations = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/import/locations', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const importOpeningBalances = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/import/opening-balances', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// Categories
export const getCategories = (species) => api.get('/categories', { params: { species } });

// Diseases
export const getDiseases = (species) => api.get('/diseases', { params: species ? { species } : {} });
export const createDisease = (data) => api.post('/diseases', data);
export const updateDisease = (id, data) => api.put(`/diseases/${id}`, data);
export const deleteDisease = (id) => api.delete(`/diseases/${id}`);

// Opening Balances
export const getOpeningBalances = (params) => api.get('/opening-balances', { params });
export const createOpeningBalance = (data) => api.post('/opening-balances', data);
export const updateOpeningBalance = (id, data) => api.put(`/opening-balances/${id}`, data);

// Daily Logs
export const getDailyLogs = (params) => api.get('/daily-logs', { params });
export const getDailyLog = (id) => api.get(`/daily-logs/${id}`);
export const createDailyLog = (data) => api.post('/daily-logs', data);
export const updateDailyLog = (id, data) => api.put(`/daily-logs/${id}`, data);
export const lockDailyLog = (id) => api.post(`/daily-logs/${id}/lock`);

// Events (Admin only)
export const createEvent = (logId, data) => api.post(`/daily-logs/${logId}/events`, data);
export const updateEvent = (id, data) => api.put(`/events/${id}`, data);
export const deleteEvent = (id) => api.delete(`/events/${id}`);

// Stock Levels
export const getStockLevels = (params) => api.get('/stock-levels', { params });

// Dashboard
export const getDashboard = (params) => api.get('/dashboard', { params });

// Reports
export const getDailyReport = (date) => api.get('/reports/daily', { params: { report_date: date } });
export const getRangeReport = (params) => api.get('/reports/range', { params });
export const getAnalyticsReport = (params) => api.get('/reports/analytics', { params });
export const getDetailedReport = (params) => api.get('/reports/detailed', { params });

// Animals
export const getAnimals = (params) => api.get('/animals', { params });
export const getAnimal = (id) => api.get(`/animals/${id}`);
export const createAnimal = (data) => api.post('/animals', data);
export const updateAnimal = (id, data) => api.put(`/animals/${id}`, data);
export const transferAnimal = (id, data) => api.post(`/animals/${id}/transfer`, data);
export const bulkImportAnimals = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/animals/bulk-import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const downloadAnimalsTemplate = () => `${API_URL}/api/templates/animals`;
export const getUntaggedCalves = (params) => api.get('/animals/untagged-calves', { params });
export const assignRegistryTag = (animalId, tagId) => api.post(`/animals/${animalId}/assign-registry-tag`, { tag_id: tagId });

// Health Intervals
export const getHealthIntervals = (params) => api.get('/health-intervals', { params });
export const createHealthInterval = (data) => api.post('/health-intervals', data);
export const updateHealthInterval = (id, data) => api.put(`/health-intervals/${id}`, data);
export const deleteHealthInterval = (id) => api.delete(`/health-intervals/${id}`);

// Farm Health Events
export const getFarmHealthEvents = (params) => api.get('/farm-health', { params });
export const getFarmHealthEventById = (id) => api.get(`/farm-health/${id}`);
export const createFarmHealthEvent = (data) => api.post('/farm-health', data);
export const updateFarmHealthEvent = (id, data) => api.put(`/farm-health/${id}`, data);
export const deleteFarmHealthEvent = (id) => api.delete(`/farm-health/${id}`);
export const getLastEvents = (params) => api.get('/farm-health/last-events', { params });
export const getUpcomingHealthEvents = (params) => api.get('/farm-health/upcoming', { params });
export const getProductSuggestions = (params) => api.get('/farm-health/product-suggestions', { params });

// Vaccine Types
export const getVaccineTypes = (species) => api.get('/vaccine-types', { params: species ? { species } : {} });
export const createVaccineType = (data) => api.post('/vaccine-types', data);
export const updateVaccineType = (id, data) => api.put(`/vaccine-types/${id}`, data);
export const deleteVaccineType = (id) => api.delete(`/vaccine-types/${id}`);
export const getLastVaccinations = () => api.get('/farm-health-events/last-vaccinations');

// Registry integration
export const searchRegistryTags = (params) => api.get('/integration/registry/tags', { params });
export const getRegistrySyncReport = (params) => api.get('/internal/sync/report', { params });
export const retryRegistrySyncRow = (outboxId) => api.post(`/internal/sync/${outboxId}/retry`);

export default api;
